import { useCallback, useEffect, useRef, useState } from 'react';
import { EarlyCheckinService } from '../services/EarlyCheckinService';
import type { EarlyCheckinFilters, EarlyCheckinRow } from '../types/earlyCheckin';
import type { PaginationInfo } from '../types/student';

const SEARCH_DEBOUNCE_MS = 400;

export interface UseEarlyCheckinsReturn {
  rows: EarlyCheckinRow[];
  pagination: PaginationInfo;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useEarlyCheckins(
  page: number = 1,
  pageSize: number = 10,
  filters: EarlyCheckinFilters = {},
): UseEarlyCheckinsReturn {
  const [rows, setRows] = useState<EarlyCheckinRow[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page,
    pageSize,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reqId = useRef(0);

  const fetchPage = useCallback(
    async (requestedPage: number, requestedPageSize: number, serverFilters: EarlyCheckinFilters) => {
      const currentReq = ++reqId.current;

      try {
        setLoading(true);
        setError(null);

        const response = await EarlyCheckinService.listPaginated(
          requestedPage,
          requestedPageSize,
          serverFilters,
        );

        if (currentReq !== reqId.current) {
          return;
        }

        setRows(response.data);
        setPagination(response.pagination);
      } catch (err: unknown) {
        if (currentReq !== reqId.current) {
          return;
        }

        setError(err instanceof Error ? err.message : 'Erro ao carregar o FUP de 8 e 15 dias');
      } finally {
        if (currentReq === reqId.current) {
          setLoading(false);
        }
      }
    },
    [],
  );

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const previousRequest = useRef<{
    search: string | undefined;
    immediateFiltersKey: string;
    pageSize: number;
  } | null>(null);

  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    const currentFilters = JSON.parse(filtersKey) as EarlyCheckinFilters;
    const { search, ...immediateFilters } = currentFilters;
    const immediateFiltersKey = JSON.stringify(immediateFilters);
    const previous = previousRequest.current;
    const onlySearchChanged = previous !== null
      && search !== previous.search
      && immediateFiltersKey === previous.immediateFiltersKey
      && pageSize === previous.pageSize;

    previousRequest.current = {
      search,
      immediateFiltersKey,
      pageSize,
    };

    if (onlySearchChanged) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchPage(page, pageSize, currentFilters);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      clearTimeout(debounceTimer.current);
      fetchPage(page, pageSize, currentFilters);
    }

    return () => clearTimeout(debounceTimer.current);
  }, [fetchPage, filtersKey, page, pageSize]);

  const refetch = useCallback(() => {
    fetchPage(page, pageSize, filters);
  }, [fetchPage, filters, page, pageSize]);

  return {
    rows,
    pagination: {
      ...pagination,
      page,
      pageSize,
    },
    loading,
    error,
    refetch,
  };
}
